import React, { useState } from 'react';

import I18n from 'utils/I18n';
import Cldr from 'utils/Cldr';
import clsx from '@strava/ui/clsx';
import Button from '@strava/ui/Button';
import TrialFeatures from './TrialFeatures';
import DurationSelectTrial from './DurationSelect';
import DurationSelect from '../Summary/DurationSelect';

import { getOrganicProduct } from '../../state/Checkout.actions';
import { useCheckoutContext } from '../../state/CheckoutContext';

import styles from './index.scss';

export const I18nPrefix = 'strava.subscriptions.checkout.summary.trial';

export const calculateTrialEndDate = (trialLength, startDate) => {
  const trialEndDate = startDate ? new Date(startDate) : new Date();
  trialEndDate.setDate(trialEndDate.getDate() + trialLength);
  return Cldr.formatDateTime(trialEndDate, {
    format: 'additional',
    type: 'yMMMd'
  });
};

export const SummaryTrials = () => {
  const { checkoutEntityState, dispatch } = useCheckoutContext();

  const { duration, experiments, trialInfo, products } = checkoutEntityState;
  const cartData = products?.[duration];
  const {
    shouldShowTrustLanguage,
    shouldShowFeatureDescription,
    shouldShowFullSizedCta,
    shouldShowBothPlansByDefault
  } = experiments;

  const [showMoreBillingOptions, setShowMoreBillingOptions] = useState(false);

  const toggleShowBillingOptions = () =>
    setShowMoreBillingOptions(!showMoreBillingOptions);

  const isYearlyDuration = cartData.duration === 'yearly';

  const totalPrimaryPrice = isYearlyDuration
    ? cartData.yearlyTotal
    : cartData.monthlyTotal;

  const renderPerMonthPrice = (price, currency) =>
    I18n.t_html(`${I18nPrefix}.per_month`, {
      price: Cldr.formatCurrency(price, currency)
    });

  const renderPerYearPrice = (price, currency) =>
    I18n.t_html(`${I18nPrefix}.per_year`, {
      price: Cldr.formatCurrency(price, currency)
    });

  const renderOverview = () => {
    if (shouldShowFeatureDescription) {
      return <TrialFeatures />;
    }
    if (shouldShowTrustLanguage && trialInfo?.trialLength) {
      return (
        <span data-testid="disclaimer">
          {I18n.t_html(`${I18nPrefix}.disclaimer`, {
            trialEndDate: calculateTrialEndDate(trialInfo.trialLength)
          })}
        </span>
      );
    }
    return (
      <span data-testid="description">
        {I18n.t_html(`${I18nPrefix}.description`)}
      </span>
    );
  };

  const { currency } = cartData;

  const totalPrimaryText = isYearlyDuration
    ? renderPerYearPrice(totalPrimaryPrice, currency)
    : renderPerMonthPrice(totalPrimaryPrice, currency);

  const billingOptionsButtonText = I18n.t(
    `${I18nPrefix}.${
      !showMoreBillingOptions ? 'more_billing_options' : 'less_billing_options'
    }`
  );

  return (
    <div className={styles.checkoutSummary}>
      <div className={`${styles.card} card`} data-testid="card">
        <div className={`${styles.inset} ${styles.overviewSection}`}>
          <div className={styles.overviewTitle}>
            {I18n.t(`${I18nPrefix}.title`, {
              days: trialInfo?.trialLength
            })}
          </div>
          <div className={styles.overviewDescription}>{renderOverview()}</div>
        </div>

        {shouldShowBothPlansByDefault && <DurationSelect />}

        <div
          className={`${styles.inset} ${styles.pricingSection}`}
          data-testid="pricingSection"
        >
          <div className={`${styles.lineItem} ${styles.billedToday}`}>
            <div className={styles.priceRow}>
              <span>{I18n.t(`${I18nPrefix}.billed.today`)}</span>
              <span>
                {Cldr.formatCurrency(cartData.billedToday, cartData.currency)}
              </span>
            </div>
          </div>

          <div
            className={`${styles.lineItem} ${styles.billedAfterTrial}`}
            data-testid="billedAfterTrial"
          >
            <div className={styles.priceRow}>
              <span>{I18n.t(`${I18nPrefix}.billed.after_trial`)}</span>
              <span>{totalPrimaryText}</span>
            </div>
            <div
              className={clsx(
                styles.moreBillingOptions,
                shouldShowFullSizedCta && styles.fullSizedCtaContainer
              )}
            >
              {!shouldShowBothPlansByDefault && shouldShowFullSizedCta && (
                <Button
                  className={styles.moreBillingOptionsButton}
                  onClick={toggleShowBillingOptions}
                  type="button"
                  variant="primary-outline"
                >
                  {billingOptionsButtonText}
                </Button>
              )}
              {!shouldShowBothPlansByDefault && !shouldShowFullSizedCta && (
                <button
                  className={`btn btn-link ${styles.moreBillingOptionsButton}`}
                  type="button"
                  onClick={toggleShowBillingOptions}
                >
                  {billingOptionsButtonText}
                </button>
              )}
              {!shouldShowBothPlansByDefault && (
                <span className={styles.actionDivider}>
                  {shouldShowFullSizedCta ? '' : '·'}
                </span>
              )}
              <button
                className={`btn btn-link ${styles.moreBillingOptionsButton}`}
                onClick={() => getOrganicProduct(checkoutEntityState, dispatch)}
                type="button"
              >
                {I18n.t(`${I18nPrefix}.add_promo_code`)}
              </button>
            </div>
          </div>

          {!shouldShowBothPlansByDefault && showMoreBillingOptions && (
            <DurationSelectTrial />
          )}
        </div>

        <div className={`${styles.inset} ${styles.footnoteSection}`}>
          {I18n.t(`${I18nPrefix}.cancel`)}
        </div>
      </div>
    </div>
  );
};

export default SummaryTrials;
